// React Imports
import { useState } from 'react'
import { useForm } from 'react-hook-form'

// React Router Imports
import { IconButton, InputAdornment, Box } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'

// Component Imports
import Link from '@components/Link'
import { FormInput } from '@/components/form'
import RadioCircle from '@menu/svg/RadioCircle'
import RadioCircleMarked from '@menu/svg/RadioCircleMarked'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { ILoginPayload, useLoginMutation, Auth } from '@/@data'
import { ExistLogindialog } from '@/components/user/modal/ExistLogindialog'

const schema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().required('Password is required'),
  remember: yup.boolean().required('Remember me is required')
})

const LoginV2 = () => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const { __ } = useTranslator()
  const loginMutation = useLoginMutation()
  const [error, setError] = useState(false)
  const [existLoginOpen, setExistLoginOpen] = useState(false)
  // Hooks
  // const navigate = useNavigate()
  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  const methods = useForm<ILoginPayload>({
    defaultValues: {
      email: '',
      password: '',
      remember: true
    },
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const onSubmit = (data: ILoginPayload) => {
    loginMutation.mutate(data, {
      onSuccess: ({ data }) => {
        Auth.handleLoginSuccess(data)
        window.location.href = '/cms'
      },
      // onError: () => setError(true)
      onError: error => {
        if (error.code === 1012) {
          window.location.href = '/cms/account-disable'
        } else if (error.code === 1020) {
          window.location.href = '/cms/account-waiting-confirm'
        } else if (error.code === 1021) {
          window.location.href = '/cms/account-block'
        } else {
          if (error.code === 1009) {
            setExistLoginOpen(true)
          } else {
            setError(true)
            methods.setError('email', { message: '' })
            methods.setError('password', { message: '' })
          }
        }
      }
    })
  }

  return (
    <>
      <div className='flex flex-col  gap-6 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset] mbs-11 mbe-11 sm:mbs-14 md:mbs-0 md:mbe-0 bs-full md:min-bs-[80dvh] min-bs-[80dvh] px-5'>
        <div className='flex flex-col gap-1 align-center justify-center items-center md:pt-[100px]'>
          <img src='/images/pages/lift-logo.png' alt='' width={100} height={46} />
          <Typography variant='h4' className='md:text-[40px] text-[36px] leading-[38px] font-bold mt-8'>
            {__('Welcome Back')}
          </Typography>
          <Typography className='text-[15px] leading-[22px] text-[##2F2B3DB2]'>
            {__('Please sign-in to your account')}
          </Typography>
        </div>

        <Box component='form' onSubmit={methods.handleSubmit(onSubmit)} className='flex flex-col gap-1 mt-5'>
          <div className='flex flex-col gap-8 mt-5'>
            <FormInput
              id='email'
              placeholder={__('Email Address')}
              name='email'
              control={methods.control}
              errorMessage={methods.formState.errors.email?.message}
              className='rounded-full'
              slotProps={{
                input: {
                  style: { padding: '5px 15px', borderRadius: '50px' },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <i className='tabler-mail' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                    </InputAdornment>
                  )
                }
              }}
              onInput={() => {
                setError(false)
              }}
            />

            <FormInput
              className='rounded-full'
              type={isPasswordShown ? 'text' : 'password'}
              id='outlined-adornment-password'
              placeholder={__('Password')}
              name='password'
              control={methods.control}
              errorMessage={methods.formState.errors.password?.message}
              slotProps={{
                input: {
                  style: { padding: '5px 15px', borderRadius: '50px' },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <i className='tabler-lock' style={{ color: '#2F2B3DE5', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      {methods.watch('password') && (
                        <IconButton size='small' onClick={() => methods.setValue('password', '')} tabIndex={-1}>
                          <i className='tabler-circle-x' />
                        </IconButton>
                      )}
                      <IconButton edge='end' onClick={handleClickShowPassword} onMouseDown={e => e.preventDefault()}>
                        <i className={isPasswordShown ? 'tabler-eye-off' : 'tabler-eye'} />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
              onInput={() => {
                setError(false)
              }}
            />
          </div>
          <FormControlLabel
            className='flex justify-start items-center justify-self-start'
            control={
              <Checkbox
                icon={<RadioCircle />}
                checkedIcon={<RadioCircleMarked />}
                checked={methods.watch('remember')}
                onChange={event => {
                  methods.setValue('remember', event.target.checked)
                }}
              />
            }
            label={
              <div className='flex items-center gap-2 text-[13px] leading-[13px] text-[#2F2B3DB2]'>
                <span>{__('Stay logged in for 30 days')}</span>
              </div>
            }
          />

          {error && (
            <Typography color='red'>
              {__(
                'We couldn’t find an account matching the email and password you entered. Please check your email and password and try again.'
              )}
            </Typography>
          )}

          <div className='flex justify-center items-center flex-wrap gap-2 py-8'>
            <Link href='/cms/forgot-password' className='text-[#6F83F6] text-[15px]'>
              {__('Forgot Password?')}
            </Link>
          </div>

          <Button fullWidth variant='contained' className='rounded-full py-3' type='submit' sx={{ height: 48 }}>
            {__('Login')}
          </Button>
        </Box>

        <div className='mt-auto text-center'>
          <span>{__('© 2025, Made with ❤️ by')}</span> <span className='text-primary'>{__('DOSI GROUP')}</span>
        </div>
      </div>

      <ExistLogindialog open={existLoginOpen} setOpen={setExistLoginOpen} />
    </>
  )
}

export default LoginV2
